import React from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getGuestBook } from '../api/guestBookApi';
import { queryKeys } from '../api/queryKeys';
import { NexusModal } from '../components/NexusModal';
import { isFeatureEnabled } from '../utils/featureFlags';
import { dateInputValue } from '../utils/date';

function monthStart(value = new Date()) {
  const date = new Date(value);
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function monthEnd(value = new Date()) {
  const date = new Date(value);
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

function dateKey(date) {
  return dateInputValue(date);
}

function weekRange(year, month, week) {
  const start = new Date(year, month, 1 + ((Number(week) - 1) * 7));
  let end = new Date(start);
  end.setDate(start.getDate() + 6);
  if (end.getMonth() !== month) end = new Date(year, month + 1, 0);
  return { start: dateKey(start), end: dateKey(end) };
}

function visibleBranches() {
  try {
    const branches = window.visibleBranchNames?.() || [];
    return Array.isArray(branches) ? branches : [];
  } catch (err) {
    return [];
  }
}

function slotLabel(value) {
  return window.slotDisplayLabel?.(value) || value || '-';
}

function metric(value) {
  const number = Number(value || 0);
  return Number.isInteger(number) ? number.toLocaleString() : number.toLocaleString(undefined, { maximumFractionDigits: 1 });
}

function guestStatus(guest) {
  const last = guest.lastBookingDate || guest.firstBookingDate || '';
  const today = dateKey(new Date());
  const inactive = last ? ((new Date(`${today}T00:00:00`) - new Date(`${last}T00:00:00`)) / 86400000) > 30 : false;
  if (inactive) return { key: 'inactive', label: 'Inactive', color: 'var(--purple)' };
  if (Number(guest.totalBookings || 0) > 1) return { key: 'returning', label: 'Returning', color: 'var(--green)' };
  return { key: 'new', label: 'New', color: 'var(--blue)' };
}

function topPreference(items = [], emptyLabel = '-') {
  const first = items?.[0];
  if (!first) return { label: emptyLabel, detail: '' };
  return {
    label: first.key || emptyLabel,
    detail: `${Number(first.count || 0).toLocaleString()} bookings - ${Number(first.pct || 0)}%`
  };
}

function csvEscape(value) {
  return `"${String(value == null ? '' : value).replace(/"/g, '""')}"`;
}

function initialsFor(name = '') {
  return String(name || 'Guest').split(/\s+/).filter(Boolean).map(part => part[0]).join('').slice(0, 2).toUpperCase() || 'G';
}

function GuestCard({ label, count, pct, sub, color, active, onClick }) {
  return (
    <button
      type="button"
      className={`stat-card ${active ? 'active' : ''}`}
      style={{ '--accent-color': color, textAlign: 'left', cursor: 'pointer' }}
      onClick={onClick}
    >
      <div className="stat-label">{label}</div>
      <div className="stat-value overview-split">
        {Number(count || 0).toLocaleString()}<small>{pct}</small>
      </div>
      <div className="stat-sub">{sub}</div>
    </button>
  );
}

function buildParams(filters) {
  const params = {
    scope: 'custom',
    start: filters.start,
    end: filters.end,
    page: filters.page || 1,
    limit: filters.limit || 50,
    search: filters.appliedSearch || '',
    type: filters.type || '',
    status: filters.status || ''
  };
  if (filters.branch) params.branch = filters.branch;
  else {
    const branches = visibleBranches();
    if (branches.length) params.branches = branches.join(',');
  }
  return params;
}

export function GuestBookPage() {
  const queryClient = useQueryClient();
  const [filters, setFilters] = React.useState(() => ({
    year: '',
    month: '',
    week: '',
    start: dateKey(monthStart()),
    end: dateKey(monthEnd()),
    type: '',
    status: '',
    branch: '',
    search: '',
    appliedSearch: '',
    page: 1,
    limit: 50
  }));
  const [selectedGuest, setSelectedGuest] = React.useState(null);
  const [exporting, setExporting] = React.useState(false);
  const params = React.useMemo(() => buildParams(filters), [filters.start, filters.end, filters.branch, filters.type, filters.status, filters.appliedSearch, filters.page, filters.limit]);
  const queryKey = React.useMemo(() => queryKeys.guestBook(params), [params]);
  const { data, isFetching, isLoading, error } = useQuery({
    queryKey,
    queryFn: () => getGuestBook(params),
    placeholderData: previous => previous
  });

  React.useEffect(() => {
    const legacy = document.getElementById('customerbook-legacy');
    const legacyHeader = document.querySelector('#page-customerbook > .page-header');
    if (legacy) legacy.hidden = true;
    if (legacyHeader) legacyHeader.hidden = true;
    const previousRenderer = window.renderCustomerGuestBook;
    window.renderCustomerGuestBook = () => {
      queryClient.invalidateQueries({ queryKey: ['guest-book'] });
    };
    return () => {
      if (legacy) legacy.hidden = false;
      if (legacyHeader) legacyHeader.hidden = false;
      window.renderCustomerGuestBook = previousRenderer;
    };
  }, [queryClient]);

  const guests = data?.guests || [];

  const totals = React.useMemo(() => {
    return {
      totalBookings: Number(data?.totals?.bookings || 0),
      totalBilling: Number(data?.totals?.billingPax || 0),
      newGuests: Number(data?.totals?.newCustomers || 0),
      returningGuests: Number(data?.totals?.returningCustomers || 0)
    };
  }, [data]);

  function setFilter(key, value) {
    setFilters(current => ({ ...current, [key]: value, page: 1 }));
  }

  function applyCalendar(type, value) {
    const today = new Date();
    let year = Number(filters.year || today.getFullYear());
    let month = filters.month;
    let start = filters.start;
    let end = filters.end;
    const next = { ...filters, [type]: value };
    if (type === 'year') {
      if (value) {
        year = Number(value);
        start = dateKey(new Date(year, 0, 1));
        end = dateKey(new Date(year, 11, 31));
      } else {
        next.month = '';
        next.week = '';
        start = dateKey(monthStart());
        end = dateKey(monthEnd());
      }
    }
    if (type === 'month') {
      next.week = '';
      if (value !== '') {
        month = value;
        start = dateKey(new Date(year, Number(month), 1));
        end = dateKey(new Date(year, Number(month) + 1, 0));
      } else {
        start = dateKey(new Date(year, 0, 1));
        end = dateKey(new Date(year, 11, 31));
      }
    }
    if (type === 'week') {
      if (value) {
        const selectedMonth = month !== '' && month != null ? Number(month) : today.getMonth();
        const range = weekRange(year, selectedMonth, value);
        start = range.start;
        end = range.end;
      } else {
        start = month !== '' && month != null ? dateKey(new Date(year, Number(month), 1)) : dateKey(new Date(year, 0, 1));
        end = month !== '' && month != null ? dateKey(new Date(year, Number(month) + 1, 0)) : dateKey(new Date(year, 11, 31));
      }
    }
    setFilters({ ...next, start, end, page: 1 });
  }

  function clearFilters() {
    setFilters({
      year: '',
      month: '',
      week: '',
      start: dateKey(monthStart()),
      end: dateKey(monthEnd()),
      type: '',
      status: '',
      branch: '',
      search: '',
      appliedSearch: '',
      page: 1,
      limit: 50
    });
  }

  async function downloadCsv() {
    setExporting(true);
    try {
      const exportData = await getGuestBook({ ...params, page: 1, limit: 200, exportAll: 1 });
      const exportGuests = exportData?.guests || [];
      if (!exportGuests.length) {
        window.toast?.('No guests to export with current filters.', 'red');
        return;
      }
      const headers = ['Guest Name', 'Phone / UUID', 'First Booking', 'Last Booking', 'Total Bookings', 'Billing PAX', 'Preferred Slot', 'Preferred Slot Type', 'Status'];
      const rows = exportGuests.map(guest => {
        const slot = topPreference(guest.slotPreference, '');
        const type = topPreference(guest.slotTypePreference, '');
        const status = guestStatus(guest);
        return [
          guest.name,
          guest.phone || guest.uuid,
          guest.firstBookingDate,
          guest.lastBookingDate,
          guest.totalBookings,
          metric(guest.billingPax),
          slotLabel(slot.label),
          type.label,
          status.label
        ].map(csvEscape).join(',');
      });
      const blob = new Blob([['\uFEFF' + headers.map(csvEscape).join(','), ...rows].join('\r\n')], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `restaurant_guest_book_${dateKey(new Date())}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      window.toast?.(`Exported ${exportGuests.length.toLocaleString()} guests to CSV`, 'green');
    } catch (err) {
      window.toast?.(err.message || 'Guest Book export failed.', 'red');
    } finally {
      setExporting(false);
    }
  }

  const totalGuestCount = Number(data?.total || data?.totals?.customers || 0);
  const pct = count => totalGuestCount ? `${Math.round((count / totalGuestCount) * 100)}%` : '0%';

  return (
    <>
      <div className="page-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap', width: '100%' }}>
          <div>
            <h1>Guest Book</h1>
            <p>Identified by Phone - UUID</p>
          </div>
          <button id="btn-customerbook-csv" onClick={downloadCsv} disabled={exporting} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 18px', background: '#fff', color: '#000', border: '1px solid rgba(0,0,0,.15)', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: 'pointer', boxShadow: '0 4px 12px rgba(0,0,0,.15)' }}>
            {exporting ? 'Preparing...' : 'Download CSV'}
          </button>
        </div>
      </div>
      <div className="filters-bar">
        <div className="filter-group"><span className="filter-label">Year</span><select value={filters.year} onChange={event => applyCalendar('year', event.target.value)}><option value="">All Years</option><option value="2024">2024</option><option value="2025">2025</option><option value="2026">2026</option><option value="2027">2027</option></select></div>
        <div className="filter-group"><span className="filter-label">Month</span><select value={filters.month} onChange={event => applyCalendar('month', event.target.value)}><option value="">All Months</option>{['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((month, index) => <option key={month} value={index}>{month}</option>)}</select></div>
        <div className="filter-group"><span className="filter-label">Week</span><select value={filters.week} onChange={event => applyCalendar('week', event.target.value)}><option value="">All Weeks</option><option value="1">Wk 1</option><option value="2">Wk 2</option><option value="3">Wk 3</option><option value="4">Wk 4</option><option value="5">Wk 5</option></select></div>
        <div className="filter-group"><span className="filter-label">From</span><input type="date" value={filters.start} onChange={event => setFilters(current => ({ ...current, year: '', month: '', week: '', start: event.target.value, page: 1 }))} /></div>
        <div className="filter-group"><span className="filter-label">To</span><input type="date" value={filters.end} onChange={event => setFilters(current => ({ ...current, year: '', month: '', week: '', end: event.target.value, page: 1 }))} /></div>
        <button className="btn-secondary" onClick={clearFilters} style={{ padding: '6px 12px', fontSize: 12 }}>Clear</button>
        <div className="filter-group"><span className="filter-label">Type & Status</span><select value={filters.type} onChange={event => setFilter('type', event.target.value)}><option value="">All Guests</option><option value="new">New Guests</option><option value="returning">Returning Guests</option></select></div>
        <div className="filter-group"><span className="filter-label">&nbsp;</span><select value={filters.status} onChange={event => setFilter('status', event.target.value)}><option value="">Statuses</option><option value="reserved">Reserved</option><option value="completed">Completed</option></select></div>
        <div className="filter-group"><span className="filter-label">Branch</span><select value={filters.branch} onChange={event => setFilter('branch', event.target.value)}><option value="">All</option>{visibleBranches().map(branch => <option key={branch} value={branch}>{branch}</option>)}</select></div>
      </div>
      <input type="text" className="search-bar" placeholder="Search by name, phone, UUID, slot, branch..." value={filters.search} onChange={event => setFilters(current => ({ ...current, search: event.target.value }))} onKeyDown={event => { if (event.key === 'Enter') setFilters(current => ({ ...current, appliedSearch: current.search, page: 1 })); }} />
      <button className="btn-secondary" onClick={() => setFilters(current => ({ ...current, appliedSearch: current.search, page: 1 }))} style={{ marginBottom: 18, padding: '8px 14px', fontSize: 12 }}>Search</button>
      {error ? <div className="empty-state"><p>{error.message || 'Guest Book failed to load.'}</p></div> : null}
      <div className="stat-grid">
        <GuestCard label="Total Guests" count={Number(data?.totals?.customers || 0)} pct={data?.totals?.customers ? '100%' : '0%'} sub="Show all guests" color="var(--green)" active={!filters.type} onClick={() => setFilter('type', '')} />
        <GuestCard label="New Guests" count={totals.newGuests} pct={pct(totals.newGuests)} sub="First-time guests" color="var(--blue)" active={filters.type === 'new'} onClick={() => setFilter('type', filters.type === 'new' ? '' : 'new')} />
        <GuestCard label="Returning Guests" count={totals.returningGuests} pct={pct(totals.returningGuests)} sub="Repeat guests" color="var(--accent)" active={filters.type === 'returning'} onClick={() => setFilter('type', filters.type === 'returning' ? '' : 'returning')} />
      </div>
      <div className="chart-card" style={{ marginTop: 16, opacity: isFetching && !isLoading ? 0.82 : 1 }}>
        <div className="chart-title">Guest Book <span>{isLoading ? 'Loading...' : `${Number(data?.total || 0).toLocaleString()} guests - ${totals.totalBookings.toLocaleString()} bookings${isFetching ? ' - refreshing' : ''}`}</span></div>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Guest Name</th><th>ID / Phone</th><th>First Booking</th><th>Last Booking</th><th>Total Bookings</th><th>Billing PAX</th><th>Preferred Slot</th><th>Status</th></tr></thead>
            <tbody>
              {guests.length ? guests.map(guest => {
                const status = guestStatus(guest);
                const slot = topPreference(guest.slotPreference, 'No slot');
                const type = topPreference(guest.slotTypePreference, 'No type');
                return (
                  <tr key={guest.uuid || guest.phone || guest.name} className="guest-table-expand" style={{ cursor: 'pointer' }} onClick={() => setSelectedGuest(guest)}>
                    <td><div style={{ display: 'flex', alignItems: 'center', gap: 12 }}><div className="guest-avatar" style={{ width: 36, height: 36, fontSize: 13 }}>{initialsFor(guest.name)}</div><div><div className="td-name" style={{ marginBottom: 2 }}>{guest.name || 'Unknown Guest'}</div><div style={{ fontSize: 10, color: status.color }}>{status.label}</div></div></div></td>
                    <td className="td-mono">{guest.phone || guest.uuid || '-'}</td>
                    <td className="td-mono">{guest.firstBookingDate || '-'}</td>
                    <td className="td-mono">{guest.lastBookingDate || '-'}</td>
                    <td style={{ color: 'var(--purple)', fontWeight: 800, fontFamily: "'DM Mono',monospace" }}>{Number(guest.totalBookings || 0).toLocaleString()}</td>
                    <td style={{ color: 'var(--accent)', fontWeight: 800, fontFamily: "'DM Mono',monospace" }}>{metric(guest.billingPax)}</td>
                    <td><div style={{ color: 'var(--green)', fontWeight: 800 }}>{slotLabel(slot.label)}</div><div className="kanban-mini">{type.label}{slot.detail ? ` - ${slot.detail}` : ''}</div></td>
                    <td><span className="call-tag" style={{ color: status.color }}>{status.label}</span></td>
                  </tr>
                );
              }) : <tr><td colSpan="8"><div className="empty-state"><p>{isLoading ? 'Loading guest book...' : 'No guests match these filters.'}</p></div></td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      <div className="pagination">
        <span className="page-info">Page {Number(data?.page || 1)} of {Number(data?.totalPages || 1)} · {Number(data?.total || 0).toLocaleString()} guests</span>
        <button className="page-btn" disabled={Number(data?.page || 1) <= 1 || isFetching} onClick={() => setFilters(current => ({ ...current, page: Math.max(1, current.page - 1) }))}>Previous</button>
        <button className="page-btn" disabled={Number(data?.page || 1) >= Number(data?.totalPages || 1) || isFetching} onClick={() => setFilters(current => ({ ...current, page: current.page + 1 }))}>Next</button>
      </div>
      <NexusModal open={Boolean(selectedGuest)} onClose={() => setSelectedGuest(null)} maxWidth={860}>
        {selectedGuest ? (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', marginBottom: 18 }}>
              <div>
                <h2 style={{ margin: 0 }}>{selectedGuest.name || 'Unknown Guest'}</h2>
                <p style={{ margin: '6px 0 0', color: 'var(--text2)' }}>{selectedGuest.phone || selectedGuest.uuid || '-'}</p>
              </div>
              <button className="btn-secondary" onClick={() => setSelectedGuest(null)}>Close</button>
            </div>
            <div className="stat-grid" style={{ marginBottom: 16 }}>
              <div className="stat-card"><div className="stat-label">Total Bookings</div><div className="stat-value">{Number(selectedGuest.totalBookings || 0).toLocaleString()}</div></div>
              <div className="stat-card" style={{ '--accent-color': 'var(--accent)' }}><div className="stat-label">Billing PAX</div><div className="stat-value">{metric(selectedGuest.billingPax)}</div></div>
              <div className="stat-card" style={{ '--accent-color': 'var(--green)' }}><div className="stat-label">Preferred Slot</div><div className="stat-value" style={{ fontSize: 18 }}>{slotLabel(topPreference(selectedGuest.slotPreference, '-').label)}</div></div>
            </div>
            <div className="table-wrap">
              <table>
                <thead><tr><th>Reservation</th><th>Date</th><th>Branch</th><th>Slot</th><th>PAX</th><th>Billing</th><th>Status</th></tr></thead>
                <tbody>
                  {(selectedGuest.bookings || []).map(item => (
                    <tr key={item.id || item.reservationId} className="guest-table-expand" style={{ cursor: 'pointer' }} onClick={() => item.id && window.openReservationModal?.(encodeURIComponent(item.id))}>
                      <td className="td-mono">#{item.reservationId || item.id || '-'}</td>
                      <td className="td-mono">{item.bookingDate || '-'}</td>
                      <td>{item.branch || '-'}</td>
                      <td>{slotLabel(item.slot)}</td>
                      <td className="td-mono">{metric(item.pax)}</td>
                      <td className="td-mono">{metric(item.billing)}</td>
                      <td>{item.status || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
      </NexusModal>
    </>
  );
}

export function GuestBookPageMount() {
  const [target, setTarget] = React.useState(null);

  React.useEffect(() => {
    setTarget(document.getElementById('react-guest-book'));
  }, []);

  if (!target || !isFeatureEnabled('USE_REACT_GUEST_BOOK')) return null;
  return createPortal(<GuestBookPage />, target);
}
